import { useNavigate } from '@tanstack/react-router'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { useFoldersMutations } from '../../hooks'
import type { Folder } from '../../stores/types'

const dialogId = 'delete-folder'

const backPathByKind: Record<string, string> = {
  project: '/projects',
  document: '/documents',
}

export const DeleteFolderDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const folder = useDialogData() as Folder
  const { closeDialog } = useDialogActions()
  const { deleteFolder } = useFoldersMutations()

  const isFolderEmpty =
    folder?.numberOfProjects === 0 && folder?.numberOfDocuments === 0

  const dialogDescription = isFolderEmpty
    ? `This will permanently delete the folder "${folder?.name}". This action cannot be undone.`
    : 'The folder is not empty. Please delete all contents inside it before deleting the folder.'

  const handleSubmit = () => {
    closeDialog(true)
    navigate({ to: backPathByKind[folder.kind] ?? '/projects' })

    deleteFolder(folder)
      .then(() => successToast('Folder deleted successfully'))
      .catch((error) => errorToast('Failed to delete folder', error))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isFolderEmpty ? 'Delete folder?' : 'Cannot delete folder'}
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            theme={isFolderEmpty ? 'outline' : 'default'}
            onClick={() => closeDialog()}
          >
            {isFolderEmpty ? 'Cancel' : 'Close'}
          </Button>
          {isFolderEmpty && (
            <Button onClick={handleSubmit} theme="danger">
              Delete Folder
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

DeleteFolderDialog.id = dialogId
